<template>
  <v-app>
    <!-- Global Loading Overlay -->
    <v-overlay v-model="globalLoading" :opacity="0.2" class="align-center justify-center">
      <loading-spinner />
    </v-overlay>

    <main class="app-content">
      <!-- Sticky view-as indicator — must sit above the header so it survives scrolling -->
      <view-as-banner />
      <div>
        <app-header
          @updateAdminUserName="updateAdminUserName"
          @refreshDashboard="refreshDashboard"
          :adminUserName="adminUserName"
        />
      </div>
      <!-- Route content -->
      <div class="main-content">
        <router-view @userName="userName" :refreshDashboardPage="refresh" v-if="!isIEBrowser" />
      </div>
      <!-- Footer -->
      <footer-component v-if="!isIEBrowser" />
    </main>
    <div v-if="isIEBrowser" class="ie-warning-modal">
      <div class="ie-warning-card">
        <div class="ie-warning-title">
          <v-icon style="color: #fec627; margin-right: 12px">mdi-alert</v-icon>
          Warning!
        </div>
        <div class="ie-warning-text">
          Sorry, this site does not support Internet Explorer. Please use our preferred browsers:
          <a href="https://www.google.com/chrome/" target="_blank">Chrome</a> or
          <a href="https://www.mozilla.org/en-US/firefox/new/" target="_blank">Firefox</a>.
        </div>
      </div>
    </div>
    <StarbucksChatUI v-if="isPartnerStarbucks" />
    <ChatComponent v-else />

    <!-- Global Notification System -->
    <GlobalNotification />

    <!-- Debug Panel (Stripe-style floating widget) -->
    <DebugPanel />

    <!-- Minor consent gate (ACC-352) -->
    <MinorConsentModal
      :show="showMinorConsentGate"
      :student-name="minorConsentStudentName"
      :dismissable="minorConsentDismissable"
      @update:show="handleMinorConsentVisibility"
      @consent="handleMinorConsentSubmit"
      @cancel="handleMinorConsentDismiss"
    />

    <!-- High school graduation date gate (ULC-3523) -->
    <HsGraduationDateModal
      :show="showHsGradDateGate"
      :viewing-as="hsGradDateViewingAs"
      :learner-name="minorConsentStudentName"
      :saving="hsGradDateSaving"
      @update:show="handleHsGradDateVisibility"
      @save="handleHsGradDateSave"
    />
  </v-app>
</template>
<script>
import { defineComponent, computed, getCurrentInstance, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAuthStore, useUiStore, useUserStore, useViewAsStore } from './stores';
import { useMinorConsentStore } from './stores/minorConsent';
import { useHsGradDateStore } from './stores/hsGradDate';
import { toReportableError } from './utils/hsGraduation';
import { useModalQueueStore } from './stores/modalQueue';
import { captureException, captureMessage } from './services/sentry.service';
import ConnectorService from './services/connector.service';
import { getPartnerHost, isPartnerHostOverridden } from './util/partner-host';

import { DataLayerMixin } from './mixins/dataLayerMixin.js';
import { markMinorConsentSatisfied } from './mixins/sessionInitializer';
import { initTours, useTour } from './tour';
import AppHeader from './components/AppHeader.vue';
import ChatComponent from './components/ChatUI.vue';
import StarbucksChatUI from './components/StarbucksChatUI.vue';
import GlobalNotification from './components/GlobalNotification.vue';
import FooterComponent from './components/FooterComponent.vue';
import DebugPanel from './components/debug/DebugPanel.vue';
import MinorConsentModal from './components/MinorConsentModal.vue';
import HsGraduationDateModal from './components/HsGraduationDateModal.vue';
import ViewAsBanner from './components/ViewAsBanner.vue';

export default defineComponent({
  name: 'App',

  data() {
    return {
      isOnline: window.navigator.onLine,
      loginCheck: true,
      value: '',
      logo: '',
      welcomeTitle: '',
      adminUserName: '',
      isIEBrowser: false,
      refresh: {
        dashboard: false
      },
      authRefreshIntervalId: null
    };
  },

  mixins: [DataLayerMixin],

  components: {
    AppHeader,
    ChatComponent,
    StarbucksChatUI,
    GlobalNotification,
    FooterComponent,
    DebugPanel,
    MinorConsentModal,
    HsGraduationDateModal,
    ViewAsBanner
  },

  props: {
    nav: {
      type: String
    }
  },

  setup() {
    const minorConsent = useMinorConsentStore();
    const userStore = useUserStore();
    const uiStore = useUiStore();
    const modalQueue = useModalQueueStore();
    const viewAsStore = useViewAsStore();
    const { blocksPortal, dismissable } = storeToRefs(minorConsent);

    // Imperative-only: safe for handlers (fresh read on every call) but not for a
    // computed, since `$conn._isProxy` isn't a reactive dependency.
    const instance = getCurrentInstance();
    const isViewingAs = () => Boolean(instance?.proxy?.$conn?._isProxy);

    // ACC-228 follow-up: an admin viewing-as a minor must never see (or be asked to
    // act on) that learner's parental-consent gate — it's silently suppressed for the
    // whole View-As session, not shown-then-dismissable. Gated on the reactive
    // viewAsStore.isActive (not isViewingAs()) so this recomputes on its own the
    // moment View-As is entered/exited, instead of depending on blocksPortal happening
    // to change value at the same time.
    const minorConsentGateActive = computed(() => blocksPortal.value && !viewAsStore.isActive);

    // ACC-408: the minor-consent gate is a hard, legal block — not a queue item.
    // Register it as a named blocker so the modal queue pauses entirely while the
    // gate is up (replacing the per-modal #1618 suppression the consumers used).
    // Suppressed during View-As along with the gate itself, so an invisible gate
    // can never leave the rest of the modal queue silently stuck.
    watch(minorConsentGateActive, (blocked) => modalQueue.setBlocked('consent-gate', blocked), { immediate: true });

    // ULC-3523: the graduation date gate waits behind the consent gate (never stacks
    // on it), then blocks the modal queue the same way. Unlike consent it is NOT
    // suppressed in View-As: the admin sees what the learner sees, and may close it.
    const hsGradDate = useHsGradDateStore();
    const hsGradDateSaving = ref(false);
    const hsGradDateGateActive = computed(() => hsGradDate.blocksPortal && !minorConsentGateActive.value);
    // Held while status loads and through the consent handoff, so no queued dialog opens under the gate.
    const hsGradDateBlocksQueue = computed(() => hsGradDate.pending || hsGradDate.blocksPortal);
    watch(hsGradDateBlocksQueue, (blocked) => modalQueue.setBlocked('hs-grad-date-gate', blocked), {
      immediate: true
    });

    // ACC-408: the product tour is the lowest-priority queue citizen (see the
    // dashboards' requestShow). Launch it only once it becomes current — i.e. once
    // every higher-priority modal has shown and been dismissed. Its own eligibility
    // gates (seen/schedule/View-As) still apply inside autoStart.
    watch(
      () => modalQueue.current,
      (id) => {
        if (id === 'product-tour') useTour().autoStart('announcements');
      }
    );

    const minorConsentStudentName = computed(() => {
      const learner = userStore.getLearnerData;
      if (!learner) return '';
      return `${learner._firstName || ''} ${learner._lastName || ''}`.trim();
    });

    // Closing the modal pauses the gate. A real learner may only do this within the
    // grace window; an admin in View-As may always close it (so a hard-blocked minor
    // doesn't trap them) — dismissForProxy is scoped to the proxy session only.
    function handleMinorConsentDismiss() {
      if (isViewingAs()) {
        minorConsent.dismissForProxy();
      } else {
        minorConsent.dismissForSession();
      }
    }

    function handleMinorConsentVisibility(visible) {
      if (!visible) handleMinorConsentDismiss();
    }

    // View-As only: closing lets the admin carry on. A real learner can't close it.
    function handleHsGradDateVisibility(visible) {
      if (!visible && (viewAsStore.isActive || isViewingAs())) hsGradDate.dismissForProxy();
    }

    async function handleHsGradDateSave(answer) {
      // Observation only: never record an answer on the learner's behalf.
      if (viewAsStore.isActive || isViewingAs()) {
        hsGradDate.dismissForProxy();
        uiStore.showInfoNotification('View As is read-only', 'Nothing was saved. The learner will still be asked.');
        return;
      }

      hsGradDateSaving.value = true;
      try {
        const status = await ConnectorService.saveHsGraduationDate(answer);
        hsGradDate.setStatus(status);
        if (status?.graduationDate && ConnectorService.learnerData) {
          ConnectorService.learnerData._highSchoolExpectedGraduationDate = `${status.graduationDate}-01`;
        }
        uiStore.showSuccessNotification(
          'Your information has been saved!',
          "We're excited to be part of your educational journey. 🎉"
        );
      } catch (err) {
        captureException(toReportableError(err, 'hs_grad_date_save'), {
          logToConsole: true,
          context: { tags: { source: 'hs_grad_date_save' } }
        });
        uiStore.showErrorNotification('Save failed', 'We could not save your graduation date. Please try again.');
      } finally {
        hsGradDateSaving.value = false;
      }
    }

    async function handleMinorConsentSubmit(parentData) {
      // View-As is observation-only (ACC-399 #1616): the button stays clickable so the
      // admin sees the real experience, but we never record parental consent on the
      // learner's behalf.
      if (isViewingAs()) return;

      // Guard: the status call should have resolved a policy; if not, we can't
      // submit. This is a precondition failure (submission was never attempted),
      // so log the specific cause for us and show the learner a plain message.
      if (!minorConsent.policyId) {
        captureMessage('Minor consent submit blocked: no resolved policyId', {
          level: 'warning',
          context: { tags: { source: 'minor_consent_submit' } }
        });
        uiStore.showErrorNotification(
          'Consent unavailable',
          'We could not load the consent details needed to continue. Please refresh and try again.'
        );
        return;
      }

      try {
        await ConnectorService.submitLearnerConsent({
          policyId: minorConsent.policyId,
          parentFirstName: parentData.firstName,
          parentLastName: parentData.lastName,
          parentEmail: parentData.email,
          parentPhone: parentData.phone,
          source: 'login'
        });
        minorConsent.clear();
        // ACC-411: consent is now recorded — invalidate the persisted "pending" cache and promote
        // the learner to the durable "satisfied" tier. Otherwise a hard refresh within the pending
        // TTL would re-hit the Tier-2 cache and re-block a learner who has already consented.
        markMinorConsentSatisfied(ConnectorService.learnerData?.learnerId);
      } catch (err) {
        captureException(err, {
          logToConsole: true,
          context: { tags: { source: 'minor_consent_submit' } }
        });
        uiStore.showErrorNotification('Submission failed', 'We could not submit your consent. Please try again.');
      }
    }

    return {
      showMinorConsentGate: minorConsentGateActive,
      minorConsentDismissable: computed(() => dismissable.value || viewAsStore.isActive),
      minorConsentStudentName,
      handleMinorConsentVisibility,
      handleMinorConsentDismiss,
      handleMinorConsentSubmit,
      showHsGradDateGate: hsGradDateGateActive,
      hsGradDateViewingAs: computed(() => viewAsStore.isActive),
      hsGradDateSaving,
      handleHsGradDateVisibility,
      handleHsGradDateSave
    };
  },

  watch: {
    isOnline: function () {
      this.$router.push({ name: !this.isOnline ? 'browser-error' : 'dashboard' });
    }
  },

  methods: {
    navigateTo(url) {
      window.open(url, '_blank');
    },
    updateAdminUserName(name) {
      this.adminUserName = name;
    },
    refreshDashboard() {
      this.refresh.dashboard = !this.refresh.dashboard;
    },
    goto(url) {
      window.open(url, '_blank');
    },
    login() {
      this.$auth.login('asurite');
    },
    async doLogOut() {
      await this.$auth.logOut();
    },
    userName(e) {
      this.adminUserName = e;
    },
    handleOnline() {
      this.isOnline = true;
    },
    handleOffline() {
      this.isOnline = false;
    }
  },
  computed: {
    isAuthenticated() {
      const authStore = useAuthStore();
      return authStore.auth;
    },
    isPartnerStarbucks() {
      // Dev workspace: the URL names no partner, so only the virtual partner host counts.
      if (isPartnerHostOverridden()) return getPartnerHost().includes('starbucks');
      return window.location.href.includes('starbucks');
    },
    globalLoading() {
      const uiStore = useUiStore();
      return uiStore.loadingState;
    }
  },

  mounted() {
    // Register tours, wire View-As suppression, and handle ?tour=<id> force-launch.
    // ACC-408: `blocked` now tracks the modal-queue blocker (the consent gate) so the
    // tour pauses for a hard block. It keeps the generic open-dialog check as a
    // backstop so the tour never stacks over a user-opened dialog (unenroll, credit
    // error, the profile form). When the tour finishes, free its queue slot.
    const modalQueue = useModalQueueStore();
    initTours({
      isViewAs: () => Boolean(this.$conn?._isProxy),
      blocked: () => modalQueue.isBlocked || Boolean(document.querySelector('.v-overlay--active.v-dialog')),
      onEvent: (event, id) => {
        // Free the tour's queue slot on any terminal outcome. `abort` matters as much
        // as complete/skip: autoStart has give-up paths (defer timeout, ineligible)
        // that never run the tour — without freeing the slot here, `current` stays
        // 'product-tour' forever and the queue can never pump another modal.
        if (id === 'announcements' && (event === 'complete' || event === 'skip' || event === 'abort')) {
          modalQueue.dismiss('product-tour');
        }
      }
    });
  },

  created() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    // calling silent auth to refresh token every 50 mins
    let self = this;
    const authStore = useAuthStore();
    this.authRefreshIntervalId = setInterval(function () {
      if (self.isAuthenticated) {
        self.$auth.silentAuth();

        self.$conn.setAuthorization();
      }
    }, authStore.REFRESH_INTERVAL_AUTH_TOKEN);

    let isIE = /*@cc_on!@*/ false || !!document.documentMode;

    if (isIE) {
      this.$router.push({ name: 'server-error' });
      this.isIEBrowser = true;
    }
  },

  beforeUnmount() {
    // Clean up interval to prevent memory leaks
    if (this.authRefreshIntervalId) {
      clearInterval(this.authRefreshIntervalId);
      this.authRefreshIntervalId = null;
    }
    // Clean up event listeners
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }
});
</script>
<style lang="stylus">
#app {
  font-family: Arial, 'Roboto', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  // text-align: center;
  // color: #2c3e50;
}

.v-content {
  max-width: 100%;
  // overflow: hidden;
}

#asu_hdr {
  z-index: 200 !important;
}

#nav {
  padding: 30px;
}

#nav a {
  font-weight: bold;
  color: #2c3e50;
}

#nav a.router-link-exact-active {
  color: #42b983;
}

.v-toolbar {
  box-shadow: none !important;
}

.v-toolbar__content {
  height: 56px !important;
}

.theme--light.application {
  background-color: rgb(255, 255, 255) !important;
}

.left-border {
  border-left: 4px solid #FFC627;
}

.fa-check-circle.fill {
  color: red;
}

.courseLabel {
  background-color: #E8E8E8;
  padding: 10px 14px;
  color: #191919;
  margin-bottom: 10px;
  font-size: 21px;
  font-weight: bold;
}

@media only screen and (max-width: 600px) {
  .courseLabel {
    strong {
      font-size: 16px;
    }

    .fa-plus-circle {
      font-size: 19px;
    }
  }
}

@media screen {
}

.p2 {
  padding: 2px;
}

.pl10 {
  padding-left: 10px;
}
</style>
<style lang="stylus">
.pac-container {
  font-family: 'Roboto', sans-serif;

  .pac-item {
    span {
      font-size: 15px;
      font-family: 'Roboto', sans-serif;
      // font-weight 400;
    }

    &:hover {
      cursor: pointer;
    }
  }

  &:after {
    /* Disclaimer: not needed to show 'powered by Google' if also a Google Map is shown */
    background-image: none !important;
    height: 0px;
  }
}

.yellowBox {
  background-color: #fec627;
  margin-top: 100px;
  border-radius: 4px;
  width: 100%;

  .boldLetters {
    font-weight: 600;
    padding-left: 78px;
    padding-top: 15px;
  }

  .para {
    padding: 0px 18px 1px;

    p {
      border-top: 3px solid rgb(243, 243, 243);
      padding-top: 5px;
      font-size: 14px;
      font-weight: 400;
    }
  }

  .line {
    height: 5px;
    border-bottom: 1px solid white;
    position: absolute;
  }
}

.courseHistory {
  i.ellipsis {
    margin-left: 1rem;
  }

  .button-check {
    color: #191919 !important;

    &.unavailable {
      color: rgb(158, 158, 158) !important;
      cursor: default;
    }
  }
}

.v-menu__content.theme--light {
  font-size: 14px;
  border-radius: 10px;

  .v-list {
    background: #f1f1f1;
  }

  .v-list-item {
    cursor: pointer;

    .v-list-item-title {
      margin-left: 10px;
    }
  }
}

.v-select__selections {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.live-chat-box-margin {
  margin-left: 24px;
  font-weight: 400;

  .theme--light.v-btn.v-btn--disabled:not(.v-btn--icon):not(.v-btn--flat):not(.v-btn--outline) {
    background: rgba(255, 226, 147, 0.5) !important;
  }
}

.live-chat-text {
  font-size: 24px;
  font-weight: 600;
}

div#banner-12 br {
  display: none;
}

@media only screen and (max-width: 1000px) {
  #asu_universal_nav_new ul {
    display: none;
  }
}

@media only screen and (min-width: 1000px) {
  .mobile-navbar-footer {
    display: none;
  }
}

.text-small {
  font-size: 14px;
  line-height: 1.4;
}

.text-dark {
  color: #191919 !important;
}

.text-center {
  text-align: center !important;
}

.nav-link {
  display: block;
  padding: 0.5rem 1rem;
}

.nav-grid-mobile {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-flow: row;
  justify-items: start;
  width: 100%;
  border-top: 1px solid #d0d0d0;
  border-right: 1px solid #d0d0d0;
  border-left: 1px solid #d0d0d0;
}

.nav-grid-mobile a {
  font-size: 14px;
  line-height: 1.4;
  color: black;
  text-align: center;
  padding: 24px 16px 24px 16px;
  width: 100%;
}

.pb-space-sm, .py-space-sm {
  padding-bottom: 1.5rem !important;
}

.search-input {
  width: 100%;
  background: url('https://unity.web.asu.edu/@asu-design-system/bootstrap4-theme/static/media/search-gray-6.86a31198.svg') no-repeat 10px 50%; // unity.web.asu.edu/@asu-design-system/bootstrap4-theme/static/media/search-gray-6.86a31198.svg) no-repeat 10px 50%;
  background-size: 16px;
  background-color: #fff;
  border: 0;
  border-radius: 0;
  padding-left: 32px;
}

.ulc-search {
  input {
    position: relative;
    padding: 10px 25px 0px 20px;
    width: 20px;
    color: #191919;
    font-size: 14px;
    font-weight: 100;
    letter-spacing: 2px;
    border: none;
    border-radius: 5px;
    background: linear-gradient(to right, #191919 0%, #191919 #191919 100%);
    transition: width 0.4s ease;
    outline: none;

    &:focus {
      width: 300px;
      color: #191919;
      font-weight: 400;
    }
  }

  i {
    position: relative;
    left: -37px;
    color: #191919;
  }

  .social {
    position: absolute;
    right: 35px;
    bottom: 35px;

    img {
      display: block;
      width: 32px;
    }
  }
}

/* IE Warning Modal Styles */
.ie-warning-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.ie-warning-card {
  background: white;
  border-radius: 4px;
  max-width: 720px;
  width: 90%;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.ie-warning-title {
  font-size: 24px;
  font-weight: 400;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ie-warning-text {
  font-weight: bold;
  line-height: 1.5;
  padding-bottom: 1.5rem;
}

.main-content {
  flex: 1;
  min-height: calc(100vh - 200px);
}

/* Mobile touch target utilities — WCAG 44px minimum */
@media (max-width: 959.98px) {
  .mobile-touch-target {
    min-height: 44px !important;
  }

  .mobile-touch-target-icon {
    min-width: 44px !important;
    min-height: 44px !important;
  }
}
</style>
